import React, { useCallback } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import { FaChevronDown } from 'react-icons/fa';
import { animated } from 'react-spring';
import styled, { css } from 'styled-components';

import TypedText from '~/shared/components/TypedText';
import {
  Button,
  Dropdown,
  Carousel,
  useBreakpoint,
  mobile,
  desktop,
} from '~/techstyle-shared/react-components';

import { useSelectActiveTab } from '../../utils/useSelectActiveTab';

const DropdownButton = styled(Dropdown.Button)`
  display: flex;
  justify-content: space-between;
  line-height: 1;
  border: 1px solid ${({ theme }) => theme.colors.palette.black};
  padding: ${({ theme: { sizes } }) => sizes.xs}px;
`;

const Chevron = styled(FaChevronDown)`
  transform: rotate(${({ $direction }) => ($direction === 'up' ? 180 : 0)}deg);
  transform-origin: 50% 50%;
`;

const SelectButton = animated(styled(Button).attrs({
  variant: 'unstyled',
})`
  padding: ${({ theme }) => `${theme.sizes.xxs}px ${theme.sizes.sm}px`};
  border: 1px solid ${({ theme }) => theme.colors.tabBorder};
  background: ${({ theme }) => theme.colors.tabDeselected};
  transition: background 0.12s 0.08s ease-in-out;

  ${mobile`
    margin: 0;
  `}

  & > span h1 {
    color: ${({ theme: { colors } }) => colors.tabDeselectedText};
  }
  & > span:before {
    content: '';
    user-select: none;
    pointer-events: none;
    ${({ $tabId }) => ($tabId === '1' ? `display: none;` : `display: block;`)}
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: ${({ theme: { sizes } }) => sizes.xxs}px;
    background: transparent;
    opacity: 1;
    transform: translateX(
      ${({ $isSelected, $tab }) => {
        if (!$isSelected && $tab === '1') {
          return '100%';
        } else if (!$isSelected && $tab === '2') {
          return '-100%';
        } else {
          return '0';
        }
      }}
    );
    transition: background 0.16s ease-in-out, opacity 0.16s ease-in-out,
      padding 0.16s ease-in-out, transform 0.16s ease-in-out;
  }

  ${({ theme }) => theme.tabsNode.scrolledTabsSelectors};

  ${({ $isSelected, $displayText, theme }) => {
    if ($isSelected) {
      if ($displayText) {
        return theme.displayTextTabSelected;
      }
      return css`
        background: ${({ theme }) => theme.colors.tabSelected};
        & > span h1 {
          color: ${({ theme }) => theme.colors.tabSelectedText};
        }
        & > span:before {
          opacity: 0;
          transform: translateX(0);
        }
      `;
    } else if ($displayText) {
      return theme.displayTextTab;
    }
  }}
`);

const StyledWrapper = styled.div`
  ${desktop`
    width: 99%;
    height: 100%;
    flex: 2;
  `}
`;

const selectorListWrapperStyle = css`
  // add more radius to outer element to match inner buttons
  border-radius: ${({ theme }) => `calc(${theme.borderRadius} + 4px)`};

  ${mobile`
    border-radius: 0;
    padding: 16px 0;
  `}

  ${({ theme }) => theme.tabsNode.scrolledTabsSelectorWrapper};
`;

const displayTextSelectorListWrapperStyle = css`
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  justify-content: center;
`;

const StyledCarousel = styled(Carousel)`
  ${selectorListWrapperStyle}
`;

const BasicWrapper = styled.div`
  ${({ $displayText }) =>
    $displayText
      ? displayTextSelectorListWrapperStyle
      : selectorListWrapperStyle}
`;

const cellStyle = css`
  width: max-content;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 8px;

  &:first-child {
    padding-left: 8px;
  }

  &:last-child {
    padding-right: 8px;
  }
`;

const SelectButtonText = styled(TypedText)`
  font-family: ${({ theme: { fontStacks } }) => fontStacks.baseFamily};
  ${({ theme: { tabsNode } }) => tabsNode.buttonText}
`;

function SelectorDropdownButton({ children }) {
  const [dropdownState] = Dropdown.useDropdownContext();

  return (
    <DropdownButton>
      {children}
      <Chevron $direction={dropdownState.isOpened ? 'up' : 'down'} />
    </DropdownButton>
  );
}

SelectorDropdownButton.propTypes = {
  children: PropTypes.node,
};

export default function Selector({
  isDisplayText,
  onOptionSelect,
  selectedTabId: selectedTabFromProps,
  selectionOptions,
}) {
  const { isMobile } = useBreakpoint();
  const selectedTabId = selectedTabFromProps || selectionOptions[0].tab;
  const foundIndex = selectionOptions.findIndex(
    ({ tab }) => tab === selectedTabId
  );

  const { flickityRefSetter } = useSelectActiveTab(foundIndex);

  const handleSelection = useCallback(
    (tabId, index) => {
      if (selectedTabId !== tabId) {
        onOptionSelect(tabId);
      }
    },
    [selectedTabId, onOptionSelect]
  );

  const scrolledDesktopBannerUsesCarousel = config.get(
    'public.brand.tabsNode.scrolledDesktopBannerUsesCarousel'
  );
  const useCarousel =
    (!isDisplayText && isMobile) || scrolledDesktopBannerUsesCarousel;
  const Wrapper = useCarousel ? StyledWrapper : React.Fragment;
  const SelectorListType = useCarousel ? StyledCarousel : BasicWrapper;
  const carouselProps = useCarousel
    ? {
        flickityRef: flickityRefSetter,
        pageDots: false,
        contain: true,
        prevNextButtons: false,
        autoPlay: false,
        percentPosition: false,
        wrapAround: false,
        cellStyle: cellStyle,
      }
    : {};

  return (
    <Wrapper>
      <SelectorListType
        {...carouselProps}
        $displayText={isDisplayText}
        data-autotag="grid-toggle-nav"
      >
        {selectionOptions.map((option, index) => {
          const { tab, content } = option;
          return (
            <SelectButton
              key={tab}
              onClick={() => handleSelection(tab, index)}
              $isSelected={tab === selectedTabId}
              $tab={tab}
              $displayText={isDisplayText}
            >
              <SelectButtonText
                type="header"
                size="14med"
                $displayText={isDisplayText}
              >
                {content}
              </SelectButtonText>
            </SelectButton>
          );
        })}
      </SelectorListType>
    </Wrapper>
  );
}

Selector.propTypes = {
  isDisplayText: PropTypes.bool,
  /**
   * A callback function called when the selected option changes, with
   * the tabId of that option.
   */
  onOptionSelect: PropTypes.func,
  selectedTabId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Array of options to choose from. Used for both the Dropdown on mobile, and
   * the desktop option buttons.
   */
  selectionOptions: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The url params value for "tab". Used to sync the selection option with
       * the corresponding ProductBrowser.
       */
      tab: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      /**
       * Most likely a FormattedMessage. Used for the DropdownListItem and
       * desktop option buttons.
       */
      content: PropTypes.any,
    })
  ),
};
